import { useState, useEffect, useMemo, useCallback } from 'react';

/**
 * Comprehensive filter hook for Payments page
 * Manages all filter states and provides filtered data
 */
export const usePaymentsFilter = () => {
  // Date/Time Filters
  const [timeFilter, setTimeFilter] = useState('all'); // 'all', 'custom', 'financial', 'monthly', 'quarterly'
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [selectedFinancialYear, setSelectedFinancialYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedQuarter, setSelectedQuarter] = useState('');
  const [selectedSubFilter, setSelectedSubFilter] = useState(''); // 'month' or 'quarter'

  // Party Filters
  const [selectedParty, setSelectedParty] = useState('');
  const [partySearchTerm, setPartySearchTerm] = useState('');

  // Payment Type Filters
  const [selectedPaymentType, setSelectedPaymentType] = useState('all'); // 'all', 'bill', 'khata'
  const [selectedPaymentMode, setSelectedPaymentMode] = useState('all'); // 'all', 'Cash', 'UPI', 'Bank', 'Cheque/DD'
  const [selectedReceiptType, setSelectedReceiptType] = useState('all'); // 'all', 'invoice', 'challan', 'purchase'

  // Amount Filters
  const [amountRange, setAmountRange] = useState({ min: '', max: '' });
  const [outstandingFilter, setOutstandingFilter] = useState('all'); // 'all', 'outstanding', 'paid', 'partial'

  // Status Filters
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'cancelled', 'draft'

  // Advanced Filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [quickFilters, setQuickFilters] = useState({
    today: false,
    thisWeek: false,
    thisMonth: false,
    thisQuarter: false,
    thisYear: false,
    last30Days: false,
    last90Days: false
  });

  // Reset all filters
  const resetFilters = useCallback(() => {
    setTimeFilter('all');
    setCustomDateFrom('');
    setCustomDateTo('');
    setSelectedFinancialYear('');
    setSelectedMonth('');
    setSelectedQuarter('');
    setSelectedSubFilter('');
    setSelectedParty('');
    setPartySearchTerm('');
    setSelectedPaymentType('all');
    setSelectedPaymentMode('all');
    setSelectedReceiptType('all');
    setAmountRange({ min: '', max: '' });
    setOutstandingFilter('all');
    setStatusFilter('all');
    setQuickFilters({
      today: false,
      thisWeek: false,
      thisMonth: false,
      thisQuarter: false,
      thisYear: false,
      last30Days: false,
      last90Days: false
    });
  }, []);

  // Get computed date range based on current filters
  const getDateRange = useCallback(() => {
    const now = new Date();
    
    // Handle quick filters first
    if (quickFilters.today) {
      return {
        start: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
        end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
      };
    }
    
    if (quickFilters.thisWeek) {
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);
      return { start: startOfWeek, end: endOfWeek };
    }
    
    if (quickFilters.thisMonth) {
      return {
        start: new Date(now.getFullYear(), now.getMonth(), 1),
        end: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59)
      };
    }
    
    if (quickFilters.thisQuarter) {
      const quarter = Math.floor(now.getMonth() / 3);
      return {
        start: new Date(now.getFullYear(), quarter * 3, 1),
        end: new Date(now.getFullYear(), quarter * 3 + 3, 0, 23, 59, 59)
      };
    }
    
    if (quickFilters.thisYear) {
      return {
        start: new Date(now.getFullYear(), 0, 1),
        end: new Date(now.getFullYear(), 11, 31, 23, 59, 59)
      };
    }
    
    if (quickFilters.last30Days) {
      const start = new Date(now);
      start.setDate(now.getDate() - 30);
      start.setHours(0, 0, 0, 0);
      return { start, end: now };
    }
    
    if (quickFilters.last90Days) {
      const start = new Date(now);
      start.setDate(now.getDate() - 90);
      start.setHours(0, 0, 0, 0);
      return { start, end: now };
    }

    // Handle custom date range
    if (timeFilter === 'custom' && customDateFrom && customDateTo) {
      return {
        start: new Date(customDateFrom),
        end: new Date(customDateTo)
      };
    }

    // Handle financial year
    if (timeFilter === 'financial' && selectedFinancialYear) {
      if (selectedSubFilter === 'month' && selectedMonth) {
        const [year, month] = selectedMonth.split('-');
        return {
          start: new Date(parseInt(year), parseInt(month) - 1, 1),
          end: new Date(parseInt(year), parseInt(month), 0, 23, 59, 59)
        };
      }
      
      if (selectedSubFilter === 'quarter' && selectedQuarter) {
        const [year, quarter] = selectedQuarter.split('-');
        const quarterStart = (parseInt(quarter) - 1) * 3;
        return {
          start: new Date(parseInt(year), quarterStart, 1),
          end: new Date(parseInt(year), quarterStart + 3, 0, 23, 59, 59)
        };
      }
      
      // Full financial year
      const year = parseInt(selectedFinancialYear);
      return {
        start: new Date(year, 3, 1), // April 1st
        end: new Date(year + 1, 2, 31, 23, 59, 59) // March 31st next year
      };
    }

    return null; // No date filter
  }, [
    timeFilter, customDateFrom, customDateTo, selectedFinancialYear, 
    selectedSubFilter, selectedMonth, selectedQuarter, quickFilters
  ]);

  // Filter bills based on all active filters
  const filterBills = useCallback((bills, parties = []) => {
    if (!bills || !Array.isArray(bills)) return [];

    let filtered = [...bills];

    // Date filter
    const dateRange = getDateRange();
    if (dateRange) {
      filtered = filtered.filter(bill => {
        const billDate = new Date(bill.invoiceDate || bill.date || bill.billDate);
        return billDate >= dateRange.start && billDate <= dateRange.end;
      });
    }

    // Party filter
    if (selectedParty) {
      filtered = filtered.filter(bill => {
        const partyId = bill.partyId || bill.party;
        return partyId === selectedParty;
      });
    }

    // Party search filter
    if (partySearchTerm) {
      const searchLower = partySearchTerm.toLowerCase();
      filtered = filtered.filter(bill => {
        const partyId = bill.partyId || bill.party;
        const party = parties.find(p => p.id === partyId);
        const partyName = party ? (party.firmName || party.name) : '';
        return partyName.toLowerCase().includes(searchLower);
      });
    }

    // Outstanding filter
    if (outstandingFilter !== 'all') {
      filtered = filtered.filter(bill => {
        const outstanding = bill.outstanding || 0;
        const totalAmount = bill.totalAmount || 0;
        
        switch (outstandingFilter) {
          case 'outstanding':
            return outstanding > 0;
          case 'paid':
            return outstanding === 0 && totalAmount > 0;
          case 'partial':
            return outstanding > 0 && outstanding < totalAmount;
          default:
            return true;
        }
      });
    }

    // Amount range filter
    if (amountRange.min !== '' || amountRange.max !== '') {
      filtered = filtered.filter(bill => {
        const amount = bill.totalAmount || 0;
        const min = amountRange.min === '' ? 0 : parseFloat(amountRange.min);
        const max = amountRange.max === '' ? Infinity : parseFloat(amountRange.max);
        return amount >= min && amount <= max;
      });
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(bill => {
        const status = bill.status || 'active';
        return status === statusFilter;
      });
    }

    return filtered;
  }, [
    getDateRange, selectedParty, partySearchTerm, outstandingFilter, 
    amountRange, statusFilter
  ]);

  // Filter payments based on all active filters
  const filterPayments = useCallback((payments) => {
    if (!payments || !Array.isArray(payments)) return [];

    let filtered = [...payments];

    // Date filter
    const dateRange = getDateRange();
    if (dateRange) {
      filtered = filtered.filter(payment => {
        const paymentDate = new Date(payment.paymentDate || payment.date);
        return paymentDate >= dateRange.start && paymentDate <= dateRange.end;
      });
    }

    // Party filter
    if (selectedParty) {
      filtered = filtered.filter(payment => {
        const partyId = payment.partyId || payment.party;
        return partyId === selectedParty;
      });
    }

    // Party search filter
    if (partySearchTerm) {
      const searchLower = partySearchTerm.toLowerCase();
      filtered = filtered.filter(payment => {
        const partyName = payment.partyName || '';
        return partyName.toLowerCase().includes(searchLower);
      });
    }

    // Payment type filter
    if (selectedPaymentType !== 'all') {
      filtered = filtered.filter(payment => {
        return payment.paymentType === selectedPaymentType;
      });
    }

    // Payment mode filter
    if (selectedPaymentMode !== 'all') {
      filtered = filtered.filter(payment => {
        return payment.paymentMode === selectedPaymentMode;
      });
    }

    // Receipt type filter
    if (selectedReceiptType !== 'all') {
      filtered = filtered.filter(payment => {
        return payment.type === selectedReceiptType;
      });
    }

    // Amount range filter
    if (amountRange.min !== '' || amountRange.max !== '') {
      filtered = filtered.filter(payment => {
        const amount = payment.totalAmount || payment.amount || 0;
        const min = amountRange.min === '' ? 0 : parseFloat(amountRange.min);
        const max = amountRange.max === '' ? Infinity : parseFloat(amountRange.max);
        return amount >= min && amount <= max;
      });
    }

    return filtered;
  }, [
    getDateRange, selectedParty, partySearchTerm, selectedPaymentType, 
    selectedPaymentMode, selectedReceiptType, amountRange
  ]);

  // Get active filter count
  const getActiveFilterCount = useCallback(() => {
    let count = 0;
    
    if (timeFilter !== 'all') count++;
    if (selectedParty) count++;
    if (partySearchTerm) count++;
    if (selectedPaymentType !== 'all') count++;
    if (selectedPaymentMode !== 'all') count++;
    if (selectedReceiptType !== 'all') count++;
    if (amountRange.min !== '' || amountRange.max !== '') count++;
    if (outstandingFilter !== 'all') count++;
    if (statusFilter !== 'all') count++;
    
    // Count quick filters
    Object.values(quickFilters).forEach(active => {
      if (active) count++;
    });
    
    return count;
  }, [
    timeFilter, selectedParty, partySearchTerm, selectedPaymentType,
    selectedPaymentMode, selectedReceiptType, amountRange, outstandingFilter,
    statusFilter, quickFilters
  ]);

  // Get filter summary for display
  const getFilterSummary = useCallback(() => {
    const summary = [];
    
    if (timeFilter !== 'all') {
      summary.push(`Time: ${timeFilter}`);
    }
    
    if (selectedParty) {
      summary.push('Party selected');
    }
    
    if (partySearchTerm) {
      summary.push(`Search: "${partySearchTerm}"`);
    }
    
    if (selectedPaymentType !== 'all') {
      summary.push(`Type: ${selectedPaymentType}`);
    }
    
    if (selectedPaymentMode !== 'all') {
      summary.push(`Mode: ${selectedPaymentMode}`);
    }
    
    if (amountRange.min !== '' || amountRange.max !== '') {
      const min = amountRange.min || '0';
      const max = amountRange.max || '∞';
      summary.push(`Amount: ${min} - ${max}`);
    }
    
    return summary;
  }, [
    timeFilter, selectedParty, partySearchTerm, selectedPaymentType,
    selectedPaymentMode, amountRange
  ]);

  return {
    // Filter states
    timeFilter,
    setTimeFilter,
    customDateFrom,
    setCustomDateFrom,
    customDateTo,
    setCustomDateTo,
    selectedFinancialYear,
    setSelectedFinancialYear,
    selectedMonth,
    setSelectedMonth,
    selectedQuarter,
    setSelectedQuarter,
    selectedSubFilter,
    setSelectedSubFilter,
    selectedParty,
    setSelectedParty,
    partySearchTerm,
    setPartySearchTerm,
    selectedPaymentType,
    setSelectedPaymentType,
    selectedPaymentMode,
    setSelectedPaymentMode,
    selectedReceiptType,
    setSelectedReceiptType,
    amountRange,
    setAmountRange,
    outstandingFilter,
    setOutstandingFilter,
    statusFilter,
    setStatusFilter,
    showAdvancedFilters,
    setShowAdvancedFilters,
    quickFilters,
    setQuickFilters,
    
    // Computed values
    getDateRange,
    filterBills,
    filterPayments,
    getActiveFilterCount,
    getFilterSummary,
    resetFilters
  };
};

export default usePaymentsFilter;
